'use client';

import React, { memo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import { CompareCategory, ComparePlansOrigin } from './types';
import { MobileComparisonCTA } from 'components/blood-vision/buy/india/IndiaDynamicContent';
import styles from './ComparePlans.module.css';

const RECOVERY_SERVICES_CATEGORY_KEY = 'sections.recovery.compareData.0.name';

interface ComparePlansProps {
  headers: string[];
  categories: CompareCategory[];
  stickyTopOffset?: number;
  forceExpanded?: boolean;
  compareOrigin?: ComparePlansOrigin;
}

type GridRowProps = React.HTMLAttributes<HTMLDivElement> & {
  $columns: number;
  $modal?: boolean;
};

const GridRow: React.FC<GridRowProps> = ({
  $columns,
  $modal,
  className,
  style,
  children,
  ...rest
}) => (
  <div
    className={`${styles.gridRow}${className ? ` ${className}` : ''}`}
    data-modal={$modal ? 'true' : undefined}
    style={
      {
        ...style,
        ['--pl-grid-cols']: `1fr repeat(${$columns}, 1fr)`,
        ['--pl-grid-cols-mobile']: `2fr repeat(${$columns}, 1fr)`,
      } as React.CSSProperties
    }
    {...rest}
  >
    {children}
  </div>
);

type HeaderRowProps = GridRowProps & {
  $stickyTop?: number;
};

const HeaderRow: React.FC<HeaderRowProps> = ({
  $stickyTop,
  className,
  style,
  children,
  ...rest
}) => (
  <GridRow
    className={`${styles.headerRow}${className ? ` ${className}` : ''}`}
    style={
      {
        ...style,
        ['--pl-sticky-top']: $stickyTop != null ? `${$stickyTop}px` : '0',
      } as React.CSSProperties
    }
    {...rest}
  >
    {children}
  </GridRow>
);

const CategoryRow: React.FC<GridRowProps> = ({
  className,
  children,
  ...rest
}) => (
  <GridRow
    className={`${styles.categoryRow}${className ? ` ${className}` : ''}`}
    {...rest}
  >
    {children}
  </GridRow>
);

const ItemRow: React.FC<GridRowProps> = ({ className, children, ...rest }) => (
  <GridRow
    className={`${styles.itemRow}${className ? ` ${className}` : ''}`}
    {...rest}
  >
    {children}
  </GridRow>
);

export const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden
  >
    <path
      d="M15.7734 5.33502L7.26562 14.335L3.39844 10.2441"
      stroke="#1539f5"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DashIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <line
      x1="5"
      y1="10"
      x2="15"
      y2="10"
      stroke="#787878"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const ChevronUpIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    style={{ transform: 'rotate(180deg)' }}
  >
    <path
      d="M4 6L8 10L12 6"
      stroke="#1539f5"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronDownIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    style={{ transform: 'rotate(-90deg)', marginLeft: '4px' }}
  >
    <path
      d="M4 6L8 10L12 6"
      stroke="#1539f5"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

type TableBodyProps = React.HTMLAttributes<HTMLDivElement> & {
  $isExpanded: boolean;
  $forceExpanded?: boolean;
};

const TableBody = React.forwardRef<HTMLDivElement, TableBodyProps>(
  function TableBody(
    { $isExpanded, $forceExpanded, className, children, ...rest },
    ref,
  ) {
    return (
      <div
        ref={ref}
        className={`${styles.tableBody}${className ? ` ${className}` : ''}`}
        data-expanded={$isExpanded ? 'true' : undefined}
        data-force-expanded={$forceExpanded ? 'true' : undefined}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

const TableBodyContent: React.FC<
  React.HTMLAttributes<HTMLDivElement> & {
    $isExpanded: boolean;
    $forceExpanded?: boolean;
  }
> = ({ $isExpanded, $forceExpanded, className, children, ...rest }) => (
  <div
    className={`${styles.tableBodyContent}${className ? ` ${className}` : ''}`}
    data-expanded={$isExpanded ? 'true' : undefined}
    data-force-expanded={$forceExpanded ? 'true' : undefined}
    {...rest}
  >
    {children}
  </div>
);

const CloseButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & { $isExpanded: boolean }
> = ({ $isExpanded, className, children, ...rest }) => (
  <button
    className={`${styles.closeButton}${className ? ` ${className}` : ''}`}
    data-expanded={$isExpanded ? 'true' : undefined}
    {...rest}
  >
    {children}
  </button>
);

const ComparePlansComponent: React.FC<ComparePlansProps> = ({
  headers,
  categories,
  stickyTopOffset,
  forceExpanded = false,
  compareOrigin,
}) => {
  const t = useTranslations('diagnostics');
  const colCount = headers.length;
  const [isExpanded, setIsExpanded] = useState(forceExpanded);
  const containerRef = useRef<HTMLDivElement>(null);
  const tableExpanded = forceExpanded || isExpanded;

  const handleExpand = () => {
    setIsExpanded(true);
    setTimeout(() => {
      if (containerRef.current) {
        window.scrollTo({
          top:
            containerRef.current.getBoundingClientRect().top +
            window.scrollY -
            80,
          behavior: 'smooth',
        });
      }
    }, 50);
  };

  const handleClose = () => {
    setIsExpanded(false);
  };

  return (
    <div
      className={styles.container}
      data-modal={forceExpanded ? 'true' : undefined}
    >
      {!forceExpanded && <hr className={styles.separator} />}
      {!forceExpanded && !tableExpanded && (
        <MobileComparisonCTA
          onClick={handleExpand}
          style={{
            marginBottom: '16px',
            borderColor: '#1539f5',
            color: '#1539f5',
            fontFamily: 'var(--font-jetbrains-mono), monospace',
            borderRadius: '40px',
            textTransform: 'uppercase',
          }}
        >
          {t('ui.comparePlans')} <ChevronDownIcon />
        </MobileComparisonCTA>
      )}
      <TableBody
        ref={containerRef}
        id="compare-plans-body"
        $isExpanded={tableExpanded}
        $forceExpanded={forceExpanded}
      >
        <TableBodyContent
          $isExpanded={tableExpanded}
          $forceExpanded={forceExpanded}
        >
          {!forceExpanded && (
            <CloseButton $isExpanded={tableExpanded} onClick={handleClose}>
              <ChevronUpIcon />
            </CloseButton>
          )}
          <HeaderRow
            $columns={colCount}
            $stickyTop={stickyTopOffset}
            $modal={forceExpanded}
          >
            <span className={styles.headerCell} />
            {headers.map((header) => (
              <span className={styles.headerCell} key={header}>
                {t(header)}
              </span>
            ))}
          </HeaderRow>

          {categories.map((category) => {
            const isRecoveryServicesCategory =
              category.name === RECOVERY_SERVICES_CATEGORY_KEY;
            const showThumbnail =
              category.thumbnailUrl &&
              !(compareOrigin === 'recovery' && isRecoveryServicesCategory);

            return (
              <div className={styles.categoryWrapper} key={category.name}>
                <CategoryRow $columns={colCount} $modal={forceExpanded}>
                  <div className={styles.categoryLabelWrapper}>
                    {showThumbnail && (
                      <CustomImage
                        className={styles.categoryThumbnail}
                        src={category.thumbnailUrl!}
                        alt={t(category.name)}
                        width={48}
                        height={48}
                      />
                    )}
                    <span className={styles.categoryLabel}>
                      {t(category.name)}
                    </span>
                  </div>
                </CategoryRow>

                <div className={styles.itemsContainer} data-modal={forceExpanded ? 'true' : undefined}>
                  {category.items.map((item) => (
                    <ItemRow
                      key={item.name}
                      $columns={colCount}
                      $modal={forceExpanded}
                    >
                      <span className={styles.itemName}>{t(item.name)}</span>
                      {item.columns.map((value, index) => (
                        <div className={styles.itemValue} key={index}>
                          {typeof value === 'string' ? (
                            <span className={styles.itemTextValue}>
                              {t(category.name) === 'Recovery Services'
                                ? t(value)
                                    .split(' ')
                                    .map((part, i) => (
                                      <React.Fragment key={i}>
                                        {i > 0 && (
                                          <>
                                            {' '}
                                            <br className={styles.mobileBreak} />
                                          </>
                                        )}
                                        {part}
                                      </React.Fragment>
                                    ))
                                : t(value)}
                            </span>
                          ) : value ? (
                            <CheckIcon />
                          ) : (
                            <DashIcon />
                          )}
                        </div>
                      ))}
                    </ItemRow>
                  ))}
                </div>
              </div>
            );
          })}
        </TableBodyContent>
      </TableBody>
    </div>
  );
};

const ComparePlans = memo(ComparePlansComponent);
ComparePlans.displayName = 'ComparePlans';

export default ComparePlans;
